import { useState, useCallback } from "react";
import { MessageSquare, X, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { sanitizeContactFormData } from "@/lib/security";
import { validateEmail, validatePhone, createSafeString } from "@/types";
import { trackConversion } from "@/config/analytics";

const FloatingContactWidget = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value.slice(0, k === "message" ? 2000 : 200) }));

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg("");

    if (!createSafeString(form.name)) return setErrorMsg("Please enter your name.");
    if (!validateEmail(form.email)) return setErrorMsg("Please enter a valid email.");
    if (!validatePhone(form.phone)) return setErrorMsg("Please enter a valid phone number.");
    if (!createSafeString(form.message)) return setErrorMsg("Please enter a message.");

    setSubmitting(true);
    setStatus("idle");
    try {
      if (!isSupabaseConfigured() || !supabase) {
        await new Promise((r) => setTimeout(r, 800));
      } else {
        const [firstName, ...rest] = form.name.trim().split(/\s+/);
        const sanitized = sanitizeContactFormData({
          firstName,
          lastName: rest.join(" ") || "—",
          email: form.email,
          company: "",
          phone: form.phone,
          message: form.message,
        });
        const { error } = await supabase.functions.invoke("send-contact-email", {
          body: { ...sanitized, honeypot },
        });
        if (error) throw error;
      }
      setStatus("success");
      trackConversion("floating_form_submit");
      setForm({ name: "", email: "", phone: "", message: "" });
      setHoneypot("");
    } catch (err) {
      setStatus("error");
      setErrorMsg("Could not send. Please try again or email sales@arabianalwaha.com.");
    } finally {
      setSubmitting(false);
    }
  }, [form, honeypot]);

  return (
    <>
      {/* Launcher button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close contact form" : "Open contact form"}
        className="fixed bottom-5 left-5 z-50 w-14 h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center shadow-elegant ring-4 ring-primary/25 hover:scale-110 transition-transform"
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 left-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm bg-card border border-border rounded-xl shadow-elegant overflow-hidden animate-in fade-in slide-in-from-bottom-4">
          <div className="bg-primary text-primary-foreground px-5 py-4">
            <h3 className="font-bold text-lg">Contact Us</h3>
            <p className="text-xs opacity-90">We'll reply within one business day.</p>
          </div>

          <div className="p-5">
            {status === "success" ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-3" />
                <p className="font-semibold text-accent mb-1">Message sent!</p>
                <p className="text-sm text-muted-foreground mb-4">Thank you — we'll be in touch shortly.</p>
                <Button variant="outline" size="sm" onClick={() => { setStatus("idle"); setOpen(false); }}>
                  Close
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input placeholder="Your name" value={form.name} onChange={update("name")} required />
                <Input type="email" placeholder="Email" value={form.email} onChange={update("email")} required />
                <Input type="tel" placeholder="Phone" value={form.phone} onChange={update("phone")} required />
                <Textarea placeholder="How can we help?" rows={4} value={form.message} onChange={update("message")} required className="resize-none" />

                {/* Honeypot */}
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-5000px", width: 1, height: 1, opacity: 0 }}
                />

                {errorMsg && (
                  <div className="flex items-start gap-2 text-sm text-destructive">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <Button type="submit" disabled={submitting} className="w-full gradient-primary text-white border-0">
                  {submitting ? "Sending..." : (<><Send className="w-4 h-4 mr-2" />Send message</>)}
                </Button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingContactWidget;